"use client";

import { cn } from "@/lib/utils";
import type { AgentSearchResult } from "@/types";

export interface AgentSearchCardProps {
  agent: AgentSearchResult;
  isSelected: boolean;
  onClick: () => void;
}

export function AgentSearchCard({ agent, isSelected, onClick }: AgentSearchCardProps) {
  return (
    <div
      className={cn(
        "px-3 py-2 cursor-pointer border-b border-border/50 transition-colors",
        "hover:bg-accent/50",
        isSelected && "bg-accent"
      )}
      onClick={onClick}
      role="option"
      aria-selected={isSelected}
    >
      {/* Header: Icon, Name, Version */}
      <div className="flex items-center gap-2 mb-1">
        <span className="text-base" role="img" aria-label="agent icon">
          {agent.icon || '🤖'}
        </span>
        <span className="font-medium text-sm text-foreground truncate">
          {agent.display_name || agent.name}
        </span>
        {agent.version && (
          <span className="text-xs text-muted-foreground ml-auto flex-shrink-0">
            v{agent.version}
          </span>
        )}
      </div>

      {/* Description */}
      <p className="text-xs text-muted-foreground line-clamp-1 mb-1.5">
        {agent.description}
      </p>

      {/* Footer: Tags + Score */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1 overflow-hidden">
          {(agent.tags || []).slice(0, 3).map((tag) => (
            <span
              key={tag}
              className="inline-flex items-center px-1.5 py-0.5 text-[10px] font-medium bg-secondary text-secondary-foreground rounded"
            >
              {tag}
            </span>
          ))}
        </div>
        <div className="flex items-center gap-2 text-[10px] text-muted-foreground flex-shrink-0">
          {agent.execution_count !== undefined && agent.execution_count > 0 && (
            <span>{agent.execution_count} runs</span>
          )}
          <span className="text-primary/70">
            {agent.match_type === 'keyword' ? 'keyword' :
             agent.match_type === 'semantic' ? 'semantic' :
             'hybrid'}
          </span>
        </div>
      </div>
    </div>
  );
}
